import React, { useEffect, useState } from "react";
import {
  FaShippingFast, FaClipboardCheck, FaCubes
} from "react-icons/fa";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import CapsuleTabs from "./CapsuleTabs";
import WebService from "../Services/WebService";
import type { RootState } from "../Services/Store";
import styles from "./SODetail.module.css";
import { toast } from "react-toastify";
import AcquireRawMaterialPanel, { MaterialEntry } from "./AcquireRawMaterialPanel";

// --- Pro-level Progress Steps ---
const STATUS_STEPS = [
  { key: "PENDING", label: "Pending", color: "#ffd400", icon: <FaCubes /> },
  { key: "IN_PROCESSING", label: "In Processing", color: "#7FB5FF", icon: <FaCubes /> },
  { key: "SHIPPED", label: "Shipped", color: "#10973b", icon: <FaShippingFast /> },
  { key: "COMPLETED", label: "Completed", color: "#222", icon: <FaClipboardCheck /> }
];

interface SaleOrderItem {
  sale_order_items_id: number;
  product_type: string;
  quantity: number;
  unit_price?: number;
  discount?: number;
  in_stock?: boolean;
  product_id?: number;
  product_name?: string;
  /** Legacy alias the API still sends alongside product_name. */
  name?: string;
}

interface AcquiredRawMaterial {
  acquisition_id: number;
  raw_material_id: number;
  raw_material_name: string;
  quantity: number;
}

interface OrderData {
  sale_order_id: number;
  customer_id: number;
  username: string;
  status: string;
  order_date?: string;
  total_amount: number;
  created_at?: string;
  sale_order_items: SaleOrderItem[];
  final_amount?: number;
  requires_raw_material_acquisition?: boolean;
}

function orderRequiresRawMaterialAcquisition(order?: OrderData | null): boolean {
  if (!order) return false;
  if (typeof order.requires_raw_material_acquisition === "boolean") {
    return order.requires_raw_material_acquisition;
  }
  return (order.sale_order_items || []).some(
    (item) => (item.product_type || "PRODUCT").toUpperCase() !== "RAW_MATERIAL"
  );
}

const OrderDetails: React.FC = () => {
  // Fix: support both admin/employee and customer route params
  const { orderId, id } = useParams<{ orderId?: string; id?: string }>();
  const user = useSelector((state: RootState) => state.auth.user);
  const saleOrderId = orderId || id;

  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [expandedCards, setExpandedCards] = useState<number[]>([]);
  const [rawMaterials, setRawMaterials] = useState<AcquiredRawMaterial[]>([]);
  const [loadingRawMat, setLoadingRawMat] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [, setEditMaterial] = useState<MaterialEntry | null>(null);
  const [designNames, setDesignNames] = useState<Record<string, string>>({});
  const [rmNames, setRmNames] = useState<Record<string, string>>({});

  const isAdminOrEmployee = user?.role_name?.toLowerCase().includes("admin") ||
    user?.role_name?.toLowerCase().includes("employee");
  const disableRawMatActions = orderData?.status === "SHIPPED" || orderData?.status === "COMPLETED";
  const requiresRawMaterialAcquisition = orderRequiresRawMaterialAcquisition(orderData);

  // --- Fetch order data ---
  useEffect(() => {
    if (!saleOrderId || !user) return;
    const isAdmin = user.role_name?.toLowerCase().includes("admin") || user.role_name?.toLowerCase().includes("employee");
    const endpoint = isAdmin
      ? `/v1/door-management/saleorders/${saleOrderId}`
      : `/v1/door-management/saleorders/my-saleorders/${saleOrderId}`;
WebService.getAPI<any>({ action: endpoint })
  .then((res: any) => {
    if (res.status_code === 200 && res.data) {
      setOrderData(res.data);
      setDesignNames(res.data.design_names || {});
      setRmNames(res.data.rm_names || {});
    } else {
      setOrderData(null);
    }
  })
  .catch(() => setOrderData(null));
  }, [saleOrderId, user]);

  // --- Fetch acquired raw materials ---
  const fetchRawMaterials = () => {
    if (!saleOrderId || !isAdminOrEmployee) return;
    if (!orderRequiresRawMaterialAcquisition(orderData)) {
      setRawMaterials([]);
      return;
    }
    setLoadingRawMat(true);
    WebService.getAPI<any>({
      action: `/v1/door-management/saleorders/acquire-raw-materials/${saleOrderId}`
    }).then((res: any) => {
      if (res.status_code === 200 && Array.isArray(res.data)) {
        setRawMaterials(res.data);
      } else {
        setRawMaterials([]);
      }
      setLoadingRawMat(false);
    }).catch(() => {
      setRawMaterials([]);
      setLoadingRawMat(false);
    });
  };

  useEffect(() => {
    fetchRawMaterials();
    // eslint-disable-next-line
  }, [saleOrderId, isAdminOrEmployee, orderData?.sale_order_id, orderData?.requires_raw_material_acquisition]);

  const handleDeleteMaterial = (acquisition_id: number) => {
    if (!acquisition_id) return;
    WebService.deleteAPI({
      action: `/v1/door-management/saleorders/acquire-raw-materials/${acquisition_id}`,
    }).then(() => {
      toast.success("Raw material entry deleted.");
      setRawMaterials((prev) => prev.filter(mat => mat.acquisition_id !== acquisition_id));
    }).catch(() =>
       toast.error("Failed to delete raw material.")
      );
  };

  const handleEditMaterial = (material: AcquiredRawMaterial) => {
    setEditMaterial({
      raw_material_id: material.raw_material_id,
      name: material.raw_material_name,
      quantity: material.quantity,
      measuring_unit: "",
      acquisition_id: material.acquisition_id,
    });
    setIsPanelOpen(true);
  };

  const currency = (amt?: number) =>
    typeof amt === "number"
      ? `₹${amt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
      : "-";

  const toggleCard = (id: number) => {
    setExpandedCards((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // --- Progress Bar Logic ---
  const getStepIdx = (status: string | undefined) =>
    STATUS_STEPS.findIndex(step => step.key === status);

  if (!orderData) {
    return <div className={styles.loader}>Loading...</div>;
  }

  const activeStepIdx = getStepIdx(orderData.status);

  return (
    <div className={styles.detailWrapper}>
      <CapsuleTabs />

      {/* --- SUPER ENHANCED Progress Bar --- */}
<div className={styles.superStepper}>
  <div className={styles.superStepperTrack}>
    {/* Track progress background */}
    <div
      className={styles.superStepperProgress}
      style={{
        width: `${activeStepIdx / (STATUS_STEPS.length - 1) * 100}%`,
      }}
    />
    {/* Steps */}
    {STATUS_STEPS.map((step, idx) => {
      const isCompleted = idx < activeStepIdx;
      const isActive = idx === activeStepIdx;
      return (
        <div
          className={[
            styles.superStep,
            isCompleted
              ? styles.completed
              : isActive
              ? styles.active
              : styles.inactive,
          ].join(" ")}
          style={{
            left: `${(idx) / (STATUS_STEPS.length - 1) * 100}%`,
            zIndex: isActive ? 3 : isCompleted ? 2 : 1,
          }}
          key={step.key}
        >
          <div className={styles.iconWrap}>
            {/* {isCompleted && (
              <span className={styles.tick}>
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24">
                  <path
                    d="M6 12.7l4 4 8-8"
                    stroke="#21a366"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            )} */}
            {step.icon}
          </div>
          <div className={styles.stepLabel}>{step.label}</div>
        </div>
      );
    })}
  </div>
</div>
      {/* --- Order Info --- */}
      <div className={styles.orderBox}>
        <div className={styles.orderMetaSplit}>
          <div className={styles.orderMetaLeft}>
            <div><strong>Order No:</strong> #ORD-{orderData.sale_order_id}</div>
            <div><strong>Customer ID:</strong> CUST-{orderData.customer_id}</div>
            <div><strong>Customer name:</strong> {orderData.username}</div>
          </div>
          <div className={styles.orderMetaRight}>
            <div><strong>Order Date:</strong> {orderData.created_at ? new Date(orderData.created_at).toLocaleDateString() : "-"}</div>
            <div><strong>Total Amount:</strong> {currency(orderData.final_amount)}</div>
          </div>
        </div>
      </div>

      {/* --- Product Cards (All Live Data, No Hardcoding) --- */}
   {!Array.isArray(orderData.sale_order_items) || orderData.sale_order_items.length === 0 ? (
  <div className={styles.orderBox} style={{ textAlign: "center", color: "#bdbdbd" }}>
    No products found for this order.
  </div>
) : (
  orderData.sale_order_items.map((item) => (
    <div className={styles.orderBox} key={item.sale_order_items_id}>
      <div
        className={styles.prodCardHeader}
        onClick={() => toggleCard(item.sale_order_items_id)}
        style={{ cursor: "pointer" }}
      >
        <div>
                <span style={{ marginRight: 8, fontWeight: 700, fontSize: 15 }}>🛠️</span>
             <span style={{ fontWeight: 600 }}>
  {item.product_name ||
    item.name ||
    (item.product_type === "PRODUCT"
      ? designNames?.[String(item.product_id)] || "Product"
      : item.product_type === "RAW_MATERIAL"
      ? rmNames?.[String(item.product_id)] || "Raw Material"
      : item.product_type || "N/A")}
</span>

                <span style={{ marginLeft: 14, fontWeight: 500, color: "#bbb" }}>
                  (ID: {item.sale_order_items_id})
                </span>
              </div>
              <div className={styles.prodStatus} style={{ background: "#eaf7ff", color: "#2979ff" }}>
                {orderData.status}
              </div>
            </div>
            {expandedCards.includes(item.sale_order_items_id) && (
              <div className={styles.prodStats}>
                <div className={styles.statItem}>
                  Qty: <b>{item.quantity ?? "—"}</b>
                </div>
                <div className={styles.statItem}>
                  Unit Price: <b>{currency(item.unit_price)}</b>
                </div>
                {/* <div className={styles.statItem}>
                  Discount: <b>{item.discount ?? 0}%</b>
                </div> */}
                <div className={styles.statItem}>
                  {item.in_stock !== undefined && (
                    <>In Stock: <b>{item.in_stock ? "Yes" : "No"}</b></>
                  )}
                </div>
              </div>
            )}
          </div>
        ))
      )}

      {/* --- Raw Material Request (production acquire for product items) --- */}
      {isAdminOrEmployee && requiresRawMaterialAcquisition && (
        <div className={styles.rawMatBox}>
          <div className={styles.rawMatTitle}>Raw Material Request</div>
          {loadingRawMat ? (
            <div style={{ padding: 24 }}>Loading...</div>
          ) : rawMaterials.length === 0 ? (
            <div style={{ padding: 24, color: "#777" }}>No raw materials acquired.</div>
          ) : (
            <table className={styles.rawMatTable}>
              <thead>
                <tr>
                  <th>Material</th>
                  <th>Quantity</th>
                  {!disableRawMatActions && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {rawMaterials.map((item) => (
                  <tr key={item.acquisition_id}>
                    <td>{item.raw_material_name}</td>
                    <td>{item.quantity}</td>
                    {!disableRawMatActions && (
                      <td>
                        <span
                          style={{
                            cursor: "pointer",
                            color: "#2979ff",
                            fontSize: 16,
                            marginRight: 14
                          }}
                          title="Edit"
                          onClick={() => handleEditMaterial(item)}
                        >
                          ✏️
                        </span>
                        <span
                          style={{
                            cursor: "pointer",
                            color: "#e74c3c",
                            fontSize: 16
                          }}
                          title="Delete"
                          onClick={() => handleDeleteMaterial(item.acquisition_id)}
                        >
                          🗑️
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* --- Slide-in Panel for Add/Edit Raw Material --- */}
      {isPanelOpen && (
        <AcquireRawMaterialPanel
          saleOrderId={Number(saleOrderId)}
          open={isPanelOpen}
          onClose={() => { setIsPanelOpen(false); setEditMaterial(null); }}
          onSuccess={() => {
            setIsPanelOpen(false);
            setEditMaterial(null);
            fetchRawMaterials();
          }}
        />
      )}
    </div>
  );
};

export default OrderDetails;
